import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Button from '../components/buttons/Button';
import ComplexCard from '../components/cards/ComplexCard';
import { generatePath, useNavigate } from 'react-router-dom';
import {
  ROUTE_COMPLEX_STATISTICS,
  ROUTE_EDIT_ARENA_INFO,
  ROUTE_FIELD_STATISTICS,
} from '../router/constants';
import { getToken } from '../utils/token';
import FieldCard from '../components/cards/FieldCard';
import { useAuth } from '../hooks/useAuth';
import OwnerBar from '../components/ui/OwnerBar';
import { COMMON_TRANSLATE_KEYS } from '../constants/translate-keys';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '../components/ui/Breadcrumb';
import ArenaServiceIcon from '../components/ui/ArenaServiceIcon';
import { Complex } from '../types';
import LoadingScreen from './LoadingScreen';

const initialPageLimit = 4;

const ArenaInfo = () => {
  const { id } = useParams<{ id: string }>();

  const { t } = useTranslation();

  const [complex, setComplex] = useState<Complex | null>(null);
  const [fields, setFields] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageLimit, setPageLimit] = useState(initialPageLimit);
  const [hasMoreFields, setHasMoreFields] = useState(false);
  const navigate = useNavigate();

  const { user } = useAuth();

  const fetchComplex = useCallback(async () => {
    if (!id) {
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/owner/complexes/${id}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      const complexData = response.data;
      setComplex(complexData);
      if (complexData) {
        fetchFields(complexData.id, 1, initialPageLimit);
      }
    } catch (error) {
      console.error('Error fetching complex:', error);
    }
  }, [id]);

  const fetchFields = async (
    complexId: string | number,
    page: number,
    limit: number
  ) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/owner/complexes/${complexId}/fields?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      setFields(response.data.fields);
      setTotalCount(response.data.totalCount);
      setHasMoreFields(response.data.hasNext);
    } catch (error) {
      console.error('Error fetching fields:', error);
    }
  };

  const loadMore = async () => {
    if (!complex) {
      return;
    }
    if (hasMoreFields) {
      const newPageLimit = pageLimit + initialPageLimit;
      setPageLimit(newPageLimit);
      await fetchFields(complex.id, 1, newPageLimit);
    }
  };

  useEffect(() => {
    fetchComplex();
  }, [fetchComplex, id]);

  const renderFeature = (
    label: string,
    feature: boolean,
    t: (key: string) => string
  ) => (
    <div className="flex flex-row justify-between my-2">
      <p className="text-textGray">{t(label)}:</p>
      <p>
        {feature ? t(COMMON_TRANSLATE_KEYS.YES) : t(COMMON_TRANSLATE_KEYS.NO)}
      </p>
    </div>
  );

  if (!complex) {
    return <LoadingScreen />;
  }

  return (
    <>
      <div className="mx-[30px]">
        <Breadcrumb
          className="sm:mb-8 mb-10"
          links={[
            {
              name: t(COMMON_TRANSLATE_KEYS.COMPLEXES),
              to: '/my-fields',
            },
          ]}
        >
          {complex.name} ({t(COMMON_TRANSLATE_KEYS.COMPLEX)})
        </Breadcrumb>
        <div>
          <ComplexCard
            title={complex.name}
            location={`${complex.city}, ${complex.country} | ${complex.address}`}
            width="xl"
            complex={complex}
          >
            <div className="flex lg:flex-row flex-col gap-4 items-center lg:flex-nowrap flex-wrap justify-end lg:w-[55%]">
              <div className="flex flex-nowrap flex-shrink-0 gap-2 justify-center">
                {complex &&
                  Object.keys(complex)
                    .filter((k) => !!complex[k as keyof Complex])
                    .map((k) => (
                      <ArenaServiceIcon
                        key={k}
                        service={k as keyof Complex}
                        width={24}
                        height={24}
                      />
                    ))}
              </div>
              <Button
                backgroundColor="bg-functionalGreen"
                width={12}
                textColor="text-gray-800"
                className="flex-nowrap whitespace-nowrap"
                onClick={() => navigate(`/arena/add-field/${complex.id}`)}
              >
                {t(COMMON_TRANSLATE_KEYS.ADD_FIELD)}
              </Button>
              <Button
                border="border-mainBlue"
                backgroundColor="bg-gray-800"
                width={12}
                textColor="text-mainBlue"
                onClick={() =>
                  navigate(`${ROUTE_EDIT_ARENA_INFO}/${complex.id}`)
                }
              >
                {t(COMMON_TRANSLATE_KEYS.EDIT_INFO)}
              </Button>
              <Button
                border="border-mainBlue"
                backgroundColor="bg-gray-800"
                width={12}
                textColor="text-mainBlue"
                className="flex-nowrap whitespace-nowrap"
                onClick={() =>
                  navigate(
                    generatePath(ROUTE_COMPLEX_STATISTICS, {
                      id: String(complex.id),
                    })
                  )
                }
              >
                {t(COMMON_TRANSLATE_KEYS.SEE_STATISTICS)}
              </Button>
            </div>
          </ComplexCard>
        </div>
        <div className="flex lg:flex-row flex-col gap-8 mt-6 text-mainWhite">
          <div className="w-full">
            {/* <OwnerBar ownerText={t(COMMON_TRANSLATE_KEYS.COMPLEX_OWNER)} /> */}
            {complex?.description && (
              <div className="flex flex-col w-full items-center gap-4">
                <p className="text-start w-full">{t(COMMON_TRANSLATE_KEYS.ADDITIONAL_INFO)}</p>
                <p className="text-center w-full border border-settingsDividerGray p-5 rounded-lg">
                  {complex.description}
                </p>
              </div>
            )}
            {totalCount > 0 ? (
              <>
                <div className="my-4">
                  {`${t(COMMON_TRANSLATE_KEYS.ALL_FIELDS)} (${totalCount})`}
                </div>
                {user?.cancelReservationHours && (
                  <p className="text-center w-full border border-settingsDividerGray p-5 rounded-lg">
                    {t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_NOTICE, {
                      hours: user.cancelReservationHours,
                      count: user.cancelReservationHours,
                    })}
                  </p>
                )}
                <div className="flex flex-wrap gap-4 my-4">
                  {fields.map((field: any) => (
                    <FieldCard
                      key={field.id}
                      title={field.name}
                      width="lg"
                      field={field}
                      complex={complex}
                    >
                      <div className="flex sm:flex-row flex-col gap-3 justify-center items-center w-full">
                        <Button
                          border="border-mainBlue"
                          backgroundColor="bg-gray-800"
                          textColor="text-mainBlue"
                          className="max-w-[23rem]"
                          onClick={() =>
                            navigate(
                              `/arena/${complex.id}/field-reservation/${field.id}`
                            )
                          }
                        >
                          {t(COMMON_TRANSLATE_KEYS.RESERVATIONS)}
                        </Button>
                        <Button
                          border="border-mainBlue"
                          backgroundColor="bg-gray-800"
                          textColor="text-mainBlue"
                          className="max-w-[23rem]"
                          onClick={() =>
                            navigate(
                              generatePath(ROUTE_FIELD_STATISTICS, {
                                id: String(complex.id),
                                fieldId: String(field.id),
                              })
                            )
                          }
                        >
                          {t(COMMON_TRANSLATE_KEYS.SEE_STATISTICS)}
                        </Button>
                      </div>
                    </FieldCard>
                  ))}
                </div>

                {hasMoreFields && (
                  <Button onClick={loadMore}>
                    {t(COMMON_TRANSLATE_KEYS.LOAD_MORE)}
                  </Button>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center w-full my-16 md:my-10">
                <p className="flex w-fit text-center">
                  {t(COMMON_TRANSLATE_KEYS.NO_FIELDS)}
                </p>
              </div>
            )}
          </div>
          {/* <div className="lg:w-1/3 text-mainWhite mb-12">
            <div>
              <h2 className="text-mainWhite">
                {t(COMMON_TRANSLATE_KEYS.CONTACT_OWNER)}
              </h2>
              <div className="flex flex-row justify-between my-2">
                <p className="text-textGray">
                  {t(COMMON_TRANSLATE_KEYS.EMAIL)} :
                </p>
                <p>{user?.email || 'N/A'}</p>
              </div>
              <div className="flex flex-row justify-between my-2">
                <p className="text-textGray">
                  {t(COMMON_TRANSLATE_KEYS.PHONE)}:
                </p>
                <p>{complex.phone}</p>
              </div>
            </div>
            <div className="mt-4">
              <h2 className="text-mainWhite">
                {t(COMMON_TRANSLATE_KEYS.COMPLEX_INFO)}
              </h2>
              <div className="mt-4">
                {renderFeature(
                  COMMON_TRANSLATE_KEYS.PARKING,
                  complex.parking,
                  t
                )}
                {renderFeature(
                  COMMON_TRANSLATE_KEYS.DRESSING_ROOM,
                  complex.dressing,
                  t
                )}
                {renderFeature(
                  COMMON_TRANSLATE_KEYS.COFFEE_SHOP,
                  complex.coffee,
                  t
                )}
                {renderFeature(
                  COMMON_TRANSLATE_KEYS.PROVIDE_BALLS,
                  complex.providesBalls,
                  t
                )}
                {renderFeature(
                  COMMON_TRANSLATE_KEYS.PROVIDE_T_SHIRTS,
                  complex.providesTShirts,
                  t
                )}
              </div>
            </div>
            {complex?.description && (
              <div className="mt-4">
                <h2 className="text-mainWhite">
                  {t(COMMON_TRANSLATE_KEYS.DESCRIPTION)}
                </h2>
                <p className="mt-4 text-textGray whitespace-pre-wrap">
                  {complex.description}
                </p>
              </div>
            )}
          </div> */}
        </div>
      </div>
    </>
  );
};

export default ArenaInfo;
